import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Eye, EyeOff, Download } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ElectricalSafetyTable, {
  ElectricalSafetySection,
  ElectricalSafetyItem,
} from "../../components/ElectricalSafetyScorecard/ElectricalSafetyTable";
import ElectricalSafetyChart from "../../components/ElectricalSafetyScorecard/ElectricalSafetyChart";
import axiosInstance from "../../common/axiosInstance";
import { DotLoader } from "../../components/loader/Loader";
import { usePremisesStore } from "../../store/store";
import { Dropdown } from "../../components/dropdown/Dropdown";

// Helper function to calculate section totals
const calculateSectionTotals = (items: ElectricalSafetyItem[]) => {
  const totalScore = items.reduce((sum, item) => sum + item.score, 0);
  const totalMaxScore = items.reduce((sum, item) => sum + item.maxScore, 0);
  const percentage = totalMaxScore > 0 ? (totalScore / totalMaxScore) * 100 : 0;
  return { totalScore, totalMaxScore, percentage };
};

// Helper function to create initial items
const createItem = (
  description: string,
  maxScore: number,
  complianceStatus: "Complied" | "Partially Complied" | "Not Complied" | "Not Applicable" | null = null
): ElectricalSafetyItem => {
  let score = 0;
  if (complianceStatus === "Complied") score = maxScore;
  else if (complianceStatus === "Partially Complied") score = maxScore / 2;
  else if (complianceStatus === "Not Applicable") score = maxScore; // Full marks for Not Applicable

  return {
    description,
    complianceStatus,
    score,
    maxScore,
    remarks: null,
  };
};

// Initial template data based on images
const getInitialSections = (): ElectricalSafetySection[] => {
  const sections: ElectricalSafetySection[] = [];

  // ELECTRICAL SHOCK PROTECTION
  const shockProtectionItems: ElectricalSafetyItem[] = [
    createItem("USE OF RCCB/RCBO/ELCB FOR ALL PDB'S, LDB's PORTABLE EQUIPMENTS & TOOLS ( 30 mA FOR THE POWER DB AND 30 or 100 or 300 mA FOR LIGHTING DB)", 2, null),
    createItem("USE OF CORRECT MILLI AMPS RCCB OR USE OF IT SYSTEM/ISOLATION TRANSFORMER IF RCCB IS NOT USED OR BYPASSED TO PROVIDE SHOCK & FIRE PROTECTION", 2, null),
    createItem("SAMPLE TESTING OF RCCB'S TO VERIFY ITS TRIPPING WITHIN THE REQUIRED TIME", 1, null),
    createItem("USAGE OF ELECTRICAL MATS WITH APPROPRIATE VOLTAGE INFRONT OF PANEL CONFIRMING TO IS 15652", 1, null),
    createItem("EARTHING OF EQUIPMENTS AS PER 3043", 1, null),
    createItem("USAGE OF DOUBLE INSULATED TOOLS", 1, null),
    createItem("ELECTRICAL SYSTEM EARTHING", 1, null),
    createItem("MARKINGS OF SHOCK PROTECTION BOUNDARY & MAINTENANCE OF SAFE WORKING CLEARANCE FROM ENERGIZED, EXPOSED ELECTRICAL EQUIPMENTS (UNQUALIFIED PERSON SHALL MAINTAIN 1 M CLEARANCE & QUALIFIED PERSON SHOULD ENTER INSIDE 0.3 M BOUNDARY ONLY AFTER WEARING SHOCK PROTECTION PPE)", 1, null),
  ];
  const shockTotals = calculateSectionTotals(shockProtectionItems);
  sections.push({
    id: "electrical-shock-protection",
    name: "ELECTRICAL SHOCK PROTECTION",
    items: shockProtectionItems,
    ...shockTotals,
  });

  // FIRE PROTECTION - ELECTRICAL ORIGIN
  const fireProtectionItems: ElectricalSafetyItem[] = [
    createItem("FIRE DETECTORS FOR ELECTRICAL INSTALLATIONS /PANEL ROOMS  AND MAINTAINED IN WORKING CONDITION", 2, null),
    createItem("THERMOGRAPHY SURVEY CONDUCTED TO ALL THE ELECTRICAL PANELS, CABLES, CABLE TRAYS, SWITCHYARD, UPS & DBs, CABLE ABOVE FALSE CEILING ONCE IN A YEAR", 4, null),
    createItem("FIRE RETARDANT CABLES/CABLES COATED WITH FIRE RETARDANT COMPOUND", 2, null),
    createItem("SEALING OF CABLE ENTRIES IN THE ELECTRICAL ROOM AND FLOOR ELECTRICAL ROOM", 3, null),
    createItem("COMBUSTABLE MATERIAL STORAGE NEAR ELECTRICAL INSTALLATIONS (INCLUDING FALSE CEILING)", 1, null),
    createItem("USAGE OF FRLS WIRES FOR LDB's & PDB's", 1, null),
    createItem("USAGE OF METTALIC OR FRLS CONDUITS", 1, null),
    createItem("SHORT CIRCUIT PROTECTIVE DEVICES USAGE AND SETTINGS ADEQUECY ACCORDING TO THE MAXIUM SHORT CIRCUIT CURRENT.", 2, null),
    createItem("OVERLOAD & EARTH FAULT PROTECTIVE DEVICES USAGE AND ITS \ SETTINGS ADEQUECY ACCORDING TO THE MAXIUM WORKING LOAD.", 1, null),
    createItem("FIRE EXTINGUISHERS IN TRANSFORMERS, PANEL ROOMS AND ITS ADEQUACY", 1, null),
    createItem("CABLE SUPPORTED ON CABLE TRAY AND TRAY NOT OVERLOADED", 1, null),
    createItem("ADEQUATE VENTILATION IN THE MAIN LT AND FLOOR ELECTRICAL ROOM", 1, null),
  ];
  const fireTotals = calculateSectionTotals(fireProtectionItems);
  sections.push({
    id: "fire-protection-electrical",
    name: "FIRE PROTECTION - ELECTRICAL ORIGIN",
    items: fireProtectionItems,
    ...fireTotals,
  });

  // LOCKOUT TAGOUT & ELECTRICAL WORK PERMIT SYSTEM
  const lotoItems: ElectricalSafetyItem[] = [
    createItem("LOCKING & TAGGING PROCEDURE (LOTO) & TRAINING/REFRESHER TRAINING FOR ALL", 2, null),
    createItem("AVAILABILITY OF ADEQUATE LOTO ACCESSORIES AND COMPONENTS", 4, null),
    createItem("ELECTRICAL WORK PERMIT PROCEDURE AND LOTO INTERGRATED WITH WORK PERMIT AND ELECTRICAL WORK PERMIT FOR EARTH EXCAVATION", 2, null),
  ];
  const lotoTotals = calculateSectionTotals(lotoItems);
  sections.push({
    id: "lockout-tagout",
    name: "LOCKOUT TAGOUT & ELECTRICAL WORK PERMIT SYSTEM",
    items: lotoItems,
    ...lotoTotals,
  });

  // ELECT SAFETY AWARENESS / TRAINING
  const trainingItems: ElectricalSafetyItem[] = [
    createItem("TRAINING ON BASIC ELECTRICAL SAFETY (SHOCK PROTECTION, FIRE PROTECTION, BATTERY ROOM HAZARDS)", 3, null),
    createItem("TRAINING IN ELECTRICAL EQUIPMENT MAINTENANCE", 2, null),
  ];
  const trainingTotals = calculateSectionTotals(trainingItems);
  sections.push({
    id: "elect-safety-awareness",
    name: "ELECT SAFETY AWARENESS / TRAINING",
    items: trainingItems,
    ...trainingTotals,
  });

  // ELECTRICAL SYSTEMS
  const electricalSystemsItems: ElectricalSafetyItem[] = [
    createItem("SAMPLE VERIFICATION OF CRITICAL ELECTRICAL STANDARD OPERATING PROCEDUCRES/WORK PERMIT/SAFETY MANUAL", 1, null),
    createItem("VERIFICATION OF ELECTRICAL EMERGENCY RESPONSE PLAN-CABLE FIRE, TRANSFORMER FIRE, SWITCH GEAR ROOM FIRE & ELECTRICAL SHOCK", 1, null),
    createItem("ELECTRICAL INTERLOCKS VERIFICATION AND MAINTAINANCE OF RECORDS THEREOF", 1, null),
    createItem("LOAD FLOW STUDY, SHORT CIRCUIT, RELAY CO-ORDINATION STUDY & ARC FLASH ANALYSIS", 1, null),
    createItem("HARMONIC ANALYSIS", 1, null),
    createItem("CHECKLIST FOR PORTABLE TOOLS CARRIED BY CONTRACTORS, INTERNAL TEAM SUCH AS WELDING MACHINE, DRILLING MACHINE, CUTTING MACHINE ETC.. AND EQUIPMENTS AND ITS CERTIFICATION. EQUIPMENT CABLE WITHOUT CABLE JOINTS", 1, null),
    createItem("ADEQUATE PPES USED BY CONTRACTORS ACCORDING TO THE ACTIVITY(GLOVES, WELDINNG HELMWT, VISOR, SHOES ETC...) AND CONTINUOUS MONITORING", 1, null),
    createItem("TO AVOID TEMPORARY TAPED JOINTS AND TO USE CABLE CONNECTORS", 1, null),
    createItem("PPM OF ELECTRICAL MAINTENANCE & HISTORY CARD MAINTENANCE", 1, null),
    createItem("ARE ALL CRITICAL EQUIPMENT LIKE TRANSFORMER, DG, UPS, HVAC, SPLIT AC,BIO MEDICAL EQUIPMENT & KITCHEN EQUIPMENT COVERED UNDER AMC", 2, null),
    createItem("LUX LEVEL - ADEQUATE OF MAIN LIGHTING AND EMERGENCY LIGHTING SYSTEM", 1, null),
  ];
  const systemsTotals = calculateSectionTotals(electricalSystemsItems);
  sections.push({
    id: "electrical-systems",
    name: "ELECTRICAL SYSTEMS",
    items: electricalSystemsItems,
    ...systemsTotals,
  });

  // ELECTRICAL STATUTORY COMPLIANCE
  const statutoryItems: ElectricalSafetyItem[] = [
    createItem("APPROVED SLD WITH LATEST LOADS", 1, null),
    createItem("DESIGNATED ELECTRICAL SAFETY OFFICER - VERIFICATION AND MAINTENANCE OF RECORDS BY ELECTRICAL SAFETY OFFICER", 1, null),
    createItem("CEA 2010 REGULATION 3 AUTHORIZED PERSONS", 1, null),
    createItem("PERIODICAL INSPECTION AND TESTING OF INSTALLATIONS - CEA 2010 REGULATION 30 & COMPLIANCE OF ELECTRICAL INSTALLATIONS WITH CEA 2010 REGULATIONS", 1, null),
    createItem("APPROVAL ELECTRICAL INSPECTOR FOR MODIFICATION OR ADDITION OF LOADS - CEA 2010 REG 43", 1, null),
    createItem("CARRYING OUT ELECTRICAL WORKS BY A CONTRACTOR WITH VALID LICENSE", 1, null),
  ];
  const statutoryTotals = calculateSectionTotals(statutoryItems);
  sections.push({
    id: "electrical-statutory-compliance",
    name: "ELECTRICAL STATUTORY COMPLIANCE",
    items: statutoryItems,
    ...statutoryTotals,
  });

  // LIGHTNING PROTECTION
  const lightningItems: ElectricalSafetyItem[] = [
    createItem("PROVISION OF APPROVED LIGHTNING PROTECTION SYSTEM AIR TERMINAL & INTERNAL SURGE PROTECTION AS PER IS/IEC 62305 WITH LIGHTNING PROTECTION ZONE DRAWING AVAILABLE.", 2, null),
    createItem("DOWN CONDUCTORS INSTALLATION AND ITS MAINTENANCE & EARTH PITS MAINTAINED AS PER IS 3043", 1, null),
  ];
  const lightningTotals = calculateSectionTotals(lightningItems);
  sections.push({
    id: "lightning-protection",
    name: "LIGHTNING PROTECTION",
    items: lightningItems,
    ...lightningTotals,
  });

  // EMERGENCY BACKUP SYSTEMS
  const emergencyItems: ElectricalSafetyItem[] = [
    createItem("STANDBY EMERGENCY GENERATING SYSTEM AVAILABLE AND MAINTAINED IN GOOD CONDITION", 3, null),
    createItem("EMERGENCY BACKUP POWER IS AVAILAIBLE TO CRITICAL LOADS, LIGHTING, FIRE PUMPS & UPS SYSTEMS", 2, null),
    createItem("FIRE ALARM CONTROL PANEL HAD IN BUILT BATTERY BACKUP / UPS POWER SUPPLY FOR 48 HRS", 2, null),
    createItem("EMERGENCY LIGHTING PROVIDED IN THE PANEL ROOM, EXIT WAYS, BATTERY ROOMS ETC;", 2, null),
    createItem("METAL CONDUIT / FIRE SURVIVAL CABLES USED FOR FIRE DETECTORS AND EMERGENCY LIGHTING CIRCUITS", 1, null),
  ];
  const emergencyTotals = calculateSectionTotals(emergencyItems);
  sections.push({
    id: "emergency-backup-systems",
    name: "EMERGENCY BACKUP SYSTEMS",
    items: emergencyItems,
    ...emergencyTotals,
  });

  // PERSONAL PROTECTIVE EQUIPMENT
  const ppeItems: ElectricalSafetyItem[] = [
    createItem("INSULATED GLOVES WITH SUITABLE WORK POTENTIAL", 3, null),
    createItem("NON CONTACT TYPE HV DETECTOR(IF APPLICABLE)", 1, null),
    createItem("SHOCK RESCUE TOOLS & EARTH DISCHARGE RODS", 2, null),
    createItem("INSULATED ELECTRICAL SAFETY SHOES (FIBER TOE) USAGE", 2, null),
    createItem("INSULATED MEASURING AND TESTING INSTRUMENTS CONDITIONS, TESTING PROBE CONDITIONS (CAT III or CAT IV)", 2, null),
  ];
  const ppeTotals = calculateSectionTotals(ppeItems);
  sections.push({
    id: "personal-protective-equipment",
    name: "PERSONAL PROTECTIVE EQUIPMENT",
    items: ppeItems,
    ...ppeTotals,
  });

  // ELECTRICAL PREVENTIVE MAINTENANCE
  const preventiveItems: ElectricalSafetyItem[] = [
    createItem("REGULAR MAINTENANCE OF POWER PANELS (HT, LT, FLOORWISE PANES & DBS) & REGULAR CLEANING OF PANELS ROOMS", 2, null),
    createItem("REGUALAR MAINTENANCE OF TRANSFORMERS (SILICA GEL CONDITION, OLTC MAINTENANCE, OIL BDV, IR TEST, VOLTAGE RATIO TEST, DGA TEST AND FURAN TEST (FURAN TEST TO BE CONDUCTED ONLY IF AGE OF TRANSFORMER GREATER THAN 15 YEARS)", 2, null),
    createItem("RELAYS CALIBRATION & LT BREAKER RELEASE CALIBRATION (ANNUAL BASIS)", 2, null),
    createItem("PERIODICAL MAINTENACE OF SPLIT AC TO CHECK FOR THE CONNECTIONS, FILTER, COOLING, COMPRESSOR CURRENT, PCB & SHORT CIRCUIT PROTECTION DEVICE MCB.", 2, null),
    createItem("LT MOTORS MAINTENANCE & REGULAR CLEANING OF TRANSFORMERS AND YARDS, HT SWITCHYARD/HT PANELS MAINT", 2, null),
    createItem("HEALTHINESS OF POWER SOCKETS (POLARITY & EARTHING CONNECTION), STANDARDIZED EXTENSION CORDS USED", 1, null),
    createItem("NEUTRAL TO EARTH VOLTAGE IN SOCKETS IS < 1 V FOR CRITICAL EQUIPMENT, UPS SOCKET AND LESS THAN 2V FOR THE OTHER POWER SOCKETS", 1, null),
  ];
  const preventiveTotals = calculateSectionTotals(preventiveItems);
  sections.push({
    id: "electrical-preventive-maintenance",
    name: "ELECTRICAL PREVENTIVE MAINTENANCE",
    items: preventiveItems,
    ...preventiveTotals,
  });

  // PREDICTIVE & PREVENTIVE MAINTENANCE TESTS
  const predictiveItems: ElectricalSafetyItem[] = [
    createItem("POLARIZATION INDEX TEST / INSULATION RESISTANCE TEST CONDUCTED FOR THE CRITICAL PANELS, MOTORS, HVAC/CHILLER PUMP MOTORS FIRE HYDRANT EQUIPMENT MOTORS & OLD CABELS TO ENSURE THE HEALTHINESS OF THE INSULATION", 2, null),
    createItem("INSPECTION, MAINTENANCE & TESTING OF BIO MEDICAL EQUIPMENT IS 17512 AND IEC 60601 & NEC 2023", 2, null),
  ];
  const predictiveTotals = calculateSectionTotals(predictiveItems);
  sections.push({
    id: "predictive-preventive-maintenance",
    name: "PREDICTIVE & PREVENTIVE MAINTENANCE TESTS",
    items: predictiveItems,
    ...predictiveTotals,
  });

  return sections;
};

const ElectricalSafetyScorecard: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = id && id !== "new";
  const isNewMode = id === "new";
  const [isViewMode, setIsViewMode] = useState<boolean>(isEditMode ? true : false);

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [scorecardName, setScorecardName] = useState<string>("");
  const [scorecardDescription, setScorecardDescription] = useState<string>("");
  const [selectedPremises, setSelectedPremises] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [showChart, setShowChart] = useState<boolean>(false);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  const { allPremises, fetchPremises } = usePremisesStore();

  const electricalChartImageGetterRef = useRef<(() => string) | null>(null);

  const [sections, setSections] = useState<ElectricalSafetySection[]>(getInitialSections());

  // Recalculate totals whenever sections change
  useEffect(() => {
    setSections((prevSections) =>
      prevSections.map((section) => ({
        ...section,
        ...calculateSectionTotals(section.items),
      }))
    );
  }, []);

  // Fetch premises on mount
  useEffect(() => {
    fetchPremises();
  }, [fetchPremises]);

  // Auto-fill date with today's date when creating new scorecard
  useEffect(() => {
    if (isNewMode && !selectedDate) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      setSelectedDate(`${year}-${month}-${day}`);
    }
  }, [isNewMode, selectedDate]);

  // Load scorecard data if editing
  useEffect(() => {
    if (isEditMode && id) {
      loadScorecard(id);
    } else if (isNewMode) {
      setSections(getInitialSections());
      setScorecardName("");
      setScorecardDescription("");
      setSelectedPremises("");
    }
  }, [id, isEditMode, isNewMode]);

  const loadScorecard = async (scorecardId: string) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/api/electrical-safety-scorecards/${scorecardId}`
      );
      const data = response.data?.data || response.data;

      if (data) {
        setScorecardName(data.name || "");
        setScorecardDescription(data.description || "");
        setSelectedPremises(data.premisesId || "");
        setSelectedDate(data.date || "");

        if (data.sections && Array.isArray(data.sections)) {
          setSections(data.sections);
        }
      }
    } catch (error: any) {
      console.error("Error loading scorecard:", error);
      alert(error.response?.data?.message || "Failed to load scorecard");
      navigate("/safety-equipment/electrical-safety-scorecard");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!scorecardName.trim()) {
      alert("Please enter a scorecard name");
      return;
    }

    if (!selectedPremises) {
      alert("Please select a premises. A specific premises is required to save this scorecard.");
      return;
    }

    // Validate remarks are provided for Partially Complied / Not Complied items
    const missingRemarks: string[] = [];
    sections.forEach((section) => {
      section.items.forEach((item, idx) => {
        if (
          (item.complianceStatus === "Partially Complied" || item.complianceStatus === "Not Complied") &&
          !item.remarks?.trim()
        ) {
          missingRemarks.push(`${section.name} → Item #${idx + 1}: "${item.description.substring(0, 60)}..."`);
        }
      });
    });

    if (missingRemarks.length > 0) {
      alert(
        `Remarks are mandatory for "Partially Complied" and "Not Complied" items.\n\nPlease add remarks for:\n${missingRemarks.slice(0, 5).join("\n")}${missingRemarks.length > 5 ? `\n...and ${missingRemarks.length - 5} more` : ""}`
      );
      return;
    }

    try {
      setSaving(true);
      const selectedPremise = allPremises.find(
        (p: any) => p._id === selectedPremises
      );
      const premisesName = selectedPremise?.premisesName || "";

      // Calculate overall totals
      const overallTotalScore = sections.reduce((sum, s) => sum + s.totalScore, 0);
      const overallMaxScore = sections.reduce((sum, s) => sum + s.totalMaxScore, 0);
      const overallPercentage = overallMaxScore > 0 ? (overallTotalScore / overallMaxScore) * 100 : 0;

      const scorecardData = {
        name: scorecardName.trim(),
        description: scorecardDescription.trim(),
        premisesId: selectedPremises || null,
        premisesName: premisesName,
        date: selectedDate || null,
        sections,
        totalScore: overallTotalScore,
        maxScore: overallMaxScore,
        percentage: overallPercentage,
      };

      if (isEditMode && id) {
        await axiosInstance.put(`/api/electrical-safety-scorecards/${id}`, scorecardData);
        alert("Scorecard updated successfully!");
      } else {
        await axiosInstance.post("/api/electrical-safety-scorecards", scorecardData);
        alert("Scorecard created successfully!");
      }

      navigate("/safety-equipment/electrical-safety-scorecard");
    } catch (error: any) {
      console.error("Error saving scorecard:", error);
      alert(error.response?.data?.message || "Failed to save scorecard");
    } finally {
      setSaving(false);
    }
  };

  const handleSectionChange = useCallback(
    (sectionId: string, itemIndex: number, updatedItem: ElectricalSafetyItem) => {
      setSections((prevSections) => {
        const newSections = prevSections.map((section) => {
          if (section.id === sectionId) {
            const newItems = [...section.items];
            // Ensure score is auto-calculated based on compliance status
            let calculatedScore = 0;
            if (updatedItem.complianceStatus === "Complied") {
              calculatedScore = updatedItem.maxScore;
            } else if (updatedItem.complianceStatus === "Partially Complied") {
              calculatedScore = updatedItem.maxScore / 2;
            } else if (updatedItem.complianceStatus === "Not Complied") {
              calculatedScore = 0;
            } else if (updatedItem.complianceStatus === "Not Applicable") {
              calculatedScore = updatedItem.maxScore; // Full marks for Not Applicable
            }

            newItems[itemIndex] = {
              ...updatedItem,
              score: calculatedScore,
            };
            const totals = calculateSectionTotals(newItems);
            return {
              ...section,
              items: newItems,
              ...totals,
            };
          }
          return section;
        });
        return newSections;
      });
    },
    []
  );

  const toggleSectionCollapse = (sectionId: string) => {
    setCollapsedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const overallTotalScore = useMemo(
    () => sections.reduce((sum, s) => sum + s.totalScore, 0),
    [sections]
  );
  const overallMaxScore = useMemo(
    () => sections.reduce((sum, s) => sum + s.totalMaxScore, 0),
    [sections]
  );
  const overallPercentage = useMemo(
    () => (overallMaxScore > 0 ? (overallTotalScore / overallMaxScore) * 100 : 0),
    [overallTotalScore, overallMaxScore]
  );

  const handleDownloadPDF = async () => {
    const sectionChartHidden = !showChart;

    if (sectionChartHidden) setShowChart(true);

    await new Promise((r) => setTimeout(r, 400));
    try {
      const doc = new jsPDF({ orientation: "landscape" });
      const premiseName = allPremises.find((p: any) => p._id === selectedPremises)?.premisesName || "N/A";

      doc.setFontSize(18);
      doc.setTextColor(0, 168, 165);
      doc.text("Electrical Safety Scorecard Report", 14, 18);
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(`Name: ${scorecardName || "N/A"}`, 14, 26);
      doc.text(`Premises: ${premiseName}`, 14, 32);
      doc.text(`Date: ${selectedDate || "N/A"}`, 14, 38);
      doc.text(`Overall Score: ${overallTotalScore.toFixed(2)} / ${overallMaxScore} (${overallPercentage.toFixed(2)}%)`, 14, 44);

      let startY = 52;

      doc.setFontSize(10);
      doc.text("Section Performance Chart", 16, startY + 6);
      if (electricalChartImageGetterRef.current) {
        try {
          const imgData = electricalChartImageGetterRef.current();
          if (imgData) {
            const pageWidth = doc.internal.pageSize.width;
            const imgWidth = pageWidth - 28;
            const imgHeight = imgWidth * (320 / 860); // chart is h-80 = shorter
            doc.addImage(imgData, "PNG", 14, startY, imgWidth, imgHeight);
            startY += imgHeight + 8;
          }
        } catch (e) {
          console.warn("Chart image is not available:", e)
        }
      }

      sections.forEach((section) => {
        doc.setFontSize(10);
        doc.setTextColor(255, 255, 255);
        doc.setFillColor(0, 100, 180);
        const pageW = doc.internal.pageSize.width;
        doc.rect(14, startY - 5, pageW - 28, 8, "F");
        doc.text(`${section.name}  |  Score: ${section.totalScore.toFixed(2)} / ${section.totalMaxScore} (${section.percentage.toFixed(2)}%)`, 16, startY);
        startY += 4;

        autoTable(doc, {
          startY,
          head: [["#", "Item Description", "Compliance Status", "Score", "Max Score", "Remarks"]],
          body: section.items.map((item, idx) => [
            idx + 1,
            item.description,
            item.complianceStatus || "N/A",
            item.score.toFixed(2),
            item.maxScore,
            item.remarks || "—",
          ]),
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [0, 168, 165] },
          columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 90 }, 2: { cellWidth: 30 }, 3: { cellWidth: 15 }, 4: { cellWidth: 15 } },
          didDrawPage: () => { startY = 20; },
        });
        startY = (doc as any).lastAutoTable.finalY + 8;
      });

      doc.save(`Electrical_Safety_Scorecard_${(scorecardName || "Report").replace(/[^a-zA-Z0-9]/g, "_")}.pdf`);
    } catch (err) {
      console.error("PDF error:", err);
      alert("Failed to generate PDF.");
    } finally {
      if (sectionChartHidden) setShowChart(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <DotLoader />
      </div>
    );
  }

  return (
    <div className="w-full px-6 py-4">
      <div className="w-full mt-20 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {isEditMode ? "Edit" : isNewMode ? "Create New" : ""} Electrical Safety Scorecard
            </h2>
            <p className="text-gray-600">
              {isNewMode
                ? "Fill in the fields below and save to create a new scorecard"
                : "View and manage electrical safety parameters and compliance scores"}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/safety-equipment/electrical-safety-scorecard")}
              className="flex items-center gap-2 py-2 px-4 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to List
            </button>
            {isEditMode && (
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 py-2 px-4 bg-[#00A8A5] text-white rounded hover:bg-[#008A87] transition-colors shadow-lg"
              >
                <Download className="w-5 h-5" />
                Download PDF
              </button>
            )}
            {isEditMode && (
              <button
                onClick={() => setIsViewMode(!isViewMode)}
                className="flex items-center gap-2 py-2 px-4 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
              >
                {isViewMode ? (
                  <>
                    <Eye className="w-5 h-5" />
                    Edit Mode
                  </>
                ) : (
                  <>
                    <EyeOff className="w-5 h-5" />
                    View Mode
                  </>
                )}
              </button>
            )}
            {(isEditMode || isNewMode) && !isViewMode && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 py-2 px-4 bg-[#00A8A5] text-white rounded hover:bg-[#008A87] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {isEditMode ? "Update" : "Save"} Scorecard
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {(isEditMode || isNewMode) && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scorecard Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={scorecardName}
                onChange={(e) => setScorecardName(e.target.value)}
                placeholder="Enter scorecard name"
                disabled={isViewMode}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent ${isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <input
                type="text"
                value={scorecardDescription}
                onChange={(e) => setScorecardDescription(e.target.value)}
                placeholder="Enter description (optional)"
                disabled={isViewMode}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent ${isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Premises <span className="text-red-500">*</span>
              </label>
              <div className={isViewMode ? "pointer-events-none opacity-60" : ""}>
                <Dropdown
                  selectedValue={selectedPremises}
                  onChange={setSelectedPremises}
                  type="premises"
                />
              </div>
              {!isViewMode && !selectedPremises && (
                <p className="text-xs text-amber-600 mt-1">⚠ A specific premises must be selected</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                disabled={isViewMode}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent ${isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
              />
            </div>
          </div>
        </div>
      )}

      {/* Overall Summary */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold mb-2">TOTAL SCORE</h3>
            <p className="text-blue-100">Overall Electrical Safety Compliance</p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold mb-1">
              {overallTotalScore.toFixed(2)} / {overallMaxScore}
            </div>
            <div className="text-2xl font-semibold">
              {overallPercentage.toFixed(2)}%
            </div>
          </div>
        </div>
      </div>

      {/* Chart Toggle */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Section Performance Chart
          </h3>
          <button
            onClick={() => setShowChart(!showChart)}
            className="flex items-center gap-2 px-4 py-2 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] transition-colors text-sm font-medium"
          >
            {showChart ? (
              <>
                <EyeOff className="w-4 h-4" />
                Hide Chart
              </>
            ) : (
              <>
                <Eye className="w-4 h-4" />
                Show Chart
              </>
            )}
          </button>
        </div>
        {showChart && (
          <div className="border-t border-gray-200 pt-6">
            <ElectricalSafetyChart
              sections={sections}
              reportName={scorecardName}
              reportDate={selectedDate}
              premisesName={allPremises.find((p: any) => p._id === selectedPremises)?.premisesName}
              onCanvasReady={(getImage) => { electricalChartImageGetterRef.current = getImage; }}
            />
          </div>
        )}
      </div>

      {/* Sections */}
      <div className="flex flex-col gap-6">
        {sections.map((section) => (
          <ElectricalSafetyTable
            key={section.id}
            section={section}
            onChange={handleSectionChange}
            isEditable={(isEditMode || isNewMode) && !isViewMode}
            isCollapsed={collapsedSections.has(section.id)}
            onToggleCollapse={() => toggleSectionCollapse(section.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default ElectricalSafetyScorecard;


